/**
 * The ship-to address a customer forwards their online purchases to.
 *
 * This is a view model, not the shape of any endpoint. `POST /warehouses` (获取仓库地址) is what
 * fills it — see `normaliseWarehouses` in legacy-api/warehouses.ts, which is the only thing that
 * knows the wire's field names, its doubled `_cn`/`_en` copy and its escaped HTML. Which warehouse
 * a screen shows, and how it is chosen, is likewise not decided here: nothing in this file selects.
 *
 * The field set follows the legacy 仓库地址 page, which presents 地址 / 邮政编码 / 姓名 / 电话号码 /
 * 备注 as separate labelled rows, each with its own copy button. `addressLine` stays one opaque
 * string: splitting it into province / city / district would invent structure the endpoint does
 * not send, and on three warehouses it is not an address at all — Malaysia, Korea and Thailand all
 * hold a WhatsApp number and a remark telling the customer to ask support for the real address.
 */

/** The forwarding network spans six origins, and only some of them are Chinese. */
export type WarehouseCountry = 'CN' | 'MY' | 'KR' | 'TH' | 'JP' | 'TW';

export interface ShipToAddress {
  readonly id: string;
  /**
   * Absent when the endpoint names a country this type has no member for.
   *
   * The wire sends a numeric country id, and the six mapped in `normaliseWarehouses` are every
   * one the warehouse list currently uses. A seventh origin would arrive here as `undefined`
   * rather than being guessed at — which costs a `lang` attribute and the per-country ordering in
   * `formatShipToAddress`, and nothing else.
   */
  readonly country?: WarehouseCountry;
  /** Primary display name in the reader's language, e.g. "GUANGZHOU SHIPPING". */
  readonly label: string;
  /** The warehouse's Chinese name, when the label above is not already it. */
  readonly labelNative?: string;
  /** 姓名 — the receiver's name, a person rather than a department. */
  readonly recipient: string;
  /** 地址, exactly as received. Never translated, never truncated. */
  readonly addressLine: string;
  /** 邮政编码 — its own field because checkout forms ask for it separately. */
  readonly postcode: string;
  /** 电话号码 */
  readonly phone: string;
  /** 备注 — the vendor's own note, in the reader's language. Empty for warehouses with none. */
  readonly remark?: string;
  /**
   * 注意事项 — the rules a customer is told they must read, one line each.
   *
   * Vendor copy, resolved to the reader's language upstream, and already carrying its own "1."
   * "2." numbering: render it as lines, not as an ordered list that would number them twice.
   */
  readonly notices: readonly string[];
  /**
   * Days a parcel is held free from arrival (`expiredays`), and days until an unclaimed one is
   * destroyed (`destroydays`). Absent where the vendor has not configured them.
   *
   * A *different clock* from anything in `notices`: those state a deadline that starts when an
   * order is quoted, this one starts when the parcel lands. Keeping them apart is the point —
   * merging them would answer the wrong question convincingly.
   */
  readonly freeStorageDays?: number;
  readonly destroyAfterDays?: number;
}

/**
 * The BCP-47 tag for text belonging to a warehouse, so a screen reader pronounces an address in
 * the language it is written in rather than reading Japanese as though it were English.
 */
const CONTENT_LANG: Record<WarehouseCountry, string> = {
  CN: 'zh',
  TW: 'zh',
  JP: 'ja',
  KR: 'ko',
  TH: 'th',
  MY: 'ms',
};

export function contentLang(country: WarehouseCountry | undefined): string | undefined {
  return country && CONTENT_LANG[country];
}

/** Collapses runs of whitespace so a missing field cannot leave a double separator behind. */
function tidy(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * The 地址 row, as legacy renders and copies it: the customer code both before and after the
 * address.
 *
 * The doubling looks redundant and is not. Warehouse staff read a label wherever the code happens
 * to survive printing, and a parcel whose code is missing cannot be matched to an account at all —
 * so legacy stamps it at both ends. Copying anything narrower would quietly undo that.
 */
export function formatAddressField(address: ShipToAddress, customerCode: string): string {
  const code = tidy(customerCode);
  return [code, tidy(address.addressLine), code].filter((part) => part.length > 0).join(' ');
}

/**
 * The 姓名 row: the receiver's name with the customer code appended, as legacy does.
 *
 * Assembled from parts rather than interpolated and patched up afterwards, so that neither an
 * absent name nor an absent code can leave a dangling full-width comma. That character is a field
 * delimiter to CJK paste-parse boxes, and a stray one splits the receiver in two.
 */
export function formatNameField(address: ShipToAddress, customerCode: string): string {
  return [tidy(address.recipient), tidy(customerCode)].filter((part) => part.length > 0).join('，');
}

/**
 * One flat line carrying the whole address, for the Dashboard's single-tap copy.
 *
 * Deliberately a different representation from the per-field rows on the Warehouses screen. That
 * screen exists to fill in a checkout form one input at a time; this exists to be pasted into the
 * paste-parse box several shopping sites offer, which splits a single line into their own fields.
 *
 * The customer code joins the receiver name, because that is what the warehouse matches an
 * arriving parcel against: "Always add your customer ID to the receiver name".
 *
 * TODO(MYP-13): the per-country field order is our reading of local convention, not something the
 * vendor has confirmed. CN leads with the receiver because that is what Taobao's paste-parse box
 * expects; JP leads with the postcode because Japanese forms are postcode-first. KR, TH and TW
 * follow the CN order for want of better information.
 */
export function formatShipToAddress(address: ShipToAddress, customerCode: string): string {
  // Built from the same parts as formatNameField rather than by rewriting its output: replacing
  // the comma there hits the FIRST one, which is the wrong one whenever a receiver name contains
  // its own (e.g. "MyPoz仓库，收货部").
  const receiver = [tidy(address.recipient), tidy(customerCode)]
    .filter((part) => part.length > 0)
    .join(' ');
  const phone = tidy(address.phone);
  const line = tidy(address.addressLine);
  const postcode = tidy(address.postcode);

  const parts =
    address.country === 'JP'
      ? [postcode, line, receiver, phone]
      : [receiver, phone, line, postcode];

  const separator = address.country === 'MY' ? ', ' : ' ';

  return parts.filter((part) => part.length > 0).join(separator);
}
